import React, { useState } from 'react';

import { createTheme, ThemeProvider } from '@mui/material/styles';

import websitelogo from '../../../../assets/logos/websitelogo.svg';

import { feedback } from '../../../services/api/apiManager';

import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';

import {
	Alert,
	Box,
	Grid,
	IconButton,
	Snackbar,
	TextField,
	Typography,
} from '@mui/material';

import Paper from '@mui/material/Paper';

import Button from '@mui/material/Button';

import PropTypes from 'prop-types';

const FeedbackPopup = ({ onClose }) => {
	const defaultTheme = createTheme();
	const [value, setValue] = useState('');
	const [feedbackText, setFeedbackText] = useState('');
	const maxFeedbackLength = 500;

	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [error401Snackbar, setError401Snackbar] = useState(false);
	const [openEmptyFieldSnackbar, setOpenEmptyFieldSnackbar] = useState(false);

	const handleSubmit = async () => {
		try {
			if (!value) {
				setOpenEmptyFieldSnackbar(true); // Show Snackbar for empty fields
				return;
			}

			const response = await feedback({
				Uid: 2147483647,
				Rating: value,
				Feedback: feedbackText,
			});
			// Optionally, you can add code here to handle the response if needed.
			setFeedbackText('');

			if (response?.status === 200) {
				setOpenSnackbar(true);
				setTimeout(() => {
					onClose(); // Assuming `onclose` is a function to close the snackbar
				}, 1500);
			} else if (response?.status === 401) {
				setError401Snackbar(true);
			} else {
				// setError401Snackbar(true);
			}
		} catch (error) {
			// Optionally, you can add code here to handle errors if needed.
			// setError401Snackbar(true);
		}
	};
	const handleSnackbarClose = () => {
		setOpenSnackbar(false);
		setError401Snackbar(false);
		setOpenEmptyFieldSnackbar(false);
	};

	return (
		<ThemeProvider theme={defaultTheme}>
			<Grid
				container
				item
				justifyContent="center"
				alignItems="center"
				sx={{ minHeight: '100vh', py: 2 }}
			>
				<Grid
					component={Paper}
					item
					xs={11.5}
					sm={8.5}
					md={6.2}
					lg={5}
					xl={4.4}
					sx={{
						background: '#fff',
						borderRadius: '12px',
						overflow: 'hidden',
						boxShadow: '0 18px 54px rgba(15, 23, 42, 0.18)',
						border: '1px solid rgba(226, 232, 240, 0.9)',
						outline: 'none !important',
						'&:focus, &:focus-visible': {
							outline: 'none !important',
						},
					}}
					tabIndex={-1}
				>
					<Box
						sx={{
							p: { xs: '16px', sm: '20px 26px 0' },
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'flex-start',
								gap: 2,
							}}
						>
							<img
								src={websitelogo}
								alt="MaxCharts"
								style={{
									maxWidth: '145px',
									height: 'auto',
									objectFit: 'contain',
								}}
							/>
							<IconButton
								aria-label="close"
								onClick={onClose}
								sx={{
									width: 38,
									height: 38,
									color: '#111827',
									background: '#f3f5f9',
									outline: 'none !important',
									'&:hover': { background: '#e9edf5' },
									'&:focus, &:focus-visible': {
										outline: 'none !important',
									},
								}}
							>
								<CloseIcon sx={{ fontSize: 21 }} />
							</IconButton>
						</Box>
						<Typography
							component="h1"
							sx={{
								fontFamily: 'Poppins',
								fontSize: { xs: '19px', md: '22px' },
								fontWeight: 700,
								color: '#1f2937',
								mt: { xs: 1.5, md: 1.75 },
								lineHeight: 1.15,
							}}
						>
							We value your feedback
						</Typography>
						<Typography
							sx={{
								fontFamily: 'Poppins',
								fontSize: { xs: '12px', md: '13px' },
								color: '#6b7280',
								mt: 0.75,
							}}
						>
							Help us improve by sharing your experience.
						</Typography>
						<Box
							sx={{
								borderTop: '1px solid #dce3ed',
								mt: { xs: 1.5, md: 1.75 },
								pt: { xs: 1.75, md: 1.75 },
							}}
						>
							<Box
								sx={{
									textAlign: 'center',
									maxWidth: 560,
									mx: 'auto',
								}}
							>
								<Typography
									sx={{
										fontFamily: 'Poppins',
										fontSize: { xs: '13px', md: '15px' },
										fontWeight: 600,
										color: '#1f2937',
										mb: 1.5,
									}}
								>
									How satisfied are you with MaxCharts?
								</Typography>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										gap: { xs: 1, sm: 1.5 },
										flexWrap: 'wrap',
									}}
								>
									{[1, 2, 3, 4, 5].map((ratingValue) => {
										const selected = Number(value) >= ratingValue;
										const StarComponent = selected
											? StarRoundedIcon
											: StarBorderRoundedIcon;

										return (
											<IconButton
												key={ratingValue}
												aria-label={`${ratingValue} star rating`}
												onClick={() => setValue(ratingValue)}
												sx={{
													width: { xs: 38, sm: 42 },
													height: { xs: 38, sm: 42 },
													background: selected ? '#dce9ff' : '#edf3fc',
													color: '#2f6fdd',
													outline: 'none !important',
													'&:hover': {
														background: '#dce9ff',
													},
													'&:focus, &:focus-visible': {
														outline: 'none !important',
													},
												}}
											>
												<StarComponent sx={{ fontSize: { xs: 24, sm: 26 } }} />
											</IconButton>
										);
									})}
								</Box>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										maxWidth: { xs: 230, sm: 310 },
										mx: 'auto',
										mt: 1,
									}}
								>
									<Typography
										sx={{
											fontFamily: 'Poppins',
											color: '#6b7280',
											fontSize: { xs: '11px', sm: '12px' },
										}}
									>
										Not satisfied
									</Typography>
									<Typography
										sx={{
											fontFamily: 'Poppins',
											color: '#6b7280',
											fontSize: { xs: '11px', sm: '12px' },
										}}
									>
										Very satisfied
									</Typography>
								</Box>
							</Box>
							<Box sx={{ mt: { xs: 2, md: 2.25 } }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<ChatBubbleOutlineIcon sx={{ color: '#2f6fdd', fontSize: 18 }} />
									<Typography
										sx={{
											fontFamily: 'Poppins',
											fontSize: { xs: '12px', md: '13px' },
											fontWeight: 600,
											color: '#1f2937',
										}}
									>
										Any suggestions or comments?
									</Typography>
								</Box>
								<Box sx={{ position: 'relative', mt: 1.25 }}>
									<TextField
										multiline
										fullWidth
										value={feedbackText}
										onChange={(e) =>
											setFeedbackText(e.target.value.slice(0, maxFeedbackLength))
										}
										minRows={2}
										placeholder="Tell us what we can do better..."
										variant="outlined"
										inputProps={{ maxLength: maxFeedbackLength }}
										sx={{
											'& .MuiOutlinedInput-root': {
												borderRadius: '9px',
												fontFamily: 'Poppins',
												fontSize: '12px',
												color: '#1f2937',
												alignItems: 'flex-start',
												p: '9px 11px 24px',
												'& fieldset': {
													borderColor: '#d7dee9',
												},
												'&:hover fieldset': {
													borderColor: '#c7d1df',
												},
												'&.Mui-focused fieldset': {
													borderColor: '#2f6fdd',
													borderWidth: '1px',
												},
											},
											'& textarea::placeholder': {
												color: '#7c8798',
												opacity: 1,
											},
										}}
									/>
									<Typography
										sx={{
											position: 'absolute',
											right: 16,
											bottom: 12,
											fontFamily: 'Poppins',
											fontSize: '11px',
											color: '#6b7280',
										}}
									>
										{feedbackText.length} / {maxFeedbackLength}
									</Typography>
								</Box>
							</Box>
						</Box>
					</Box>
					<Box
						sx={{
							mt: { xs: 1.5, md: 1.75 },
							borderTop: '1px solid #dce3ed',
							background: '#fbfcfe',
							px: { xs: 2, sm: 3 },
							py: { xs: 1.5, sm: 1.75 },
							display: 'flex',
							justifyContent: 'flex-end',
							gap: 1.5,
							flexWrap: 'wrap',
						}}
					>
						<Button
							variant="outlined"
							onClick={onClose}
							sx={{
								minWidth: 104,
								height: 38,
								borderRadius: '8px',
								borderColor: '#cfd7e3',
								color: '#374151',
								fontFamily: 'Poppins',
								fontSize: '12px',
								fontWeight: 700,
								outline: 'none !important',
								'&:hover': {
									borderColor: '#b8c3d3',
									background: '#f8fafc',
								},
								'&:focus, &:focus-visible': {
									outline: 'none !important',
								},
							}}
						>
							CANCEL
						</Button>
						<Button
							variant="contained"
							onClick={() => handleSubmit()}
							startIcon={<SendOutlinedIcon sx={{ fontSize: 18 }} />}
							sx={{
								minWidth: 112,
								height: 38,
								borderRadius: '8px',
								background: 'linear-gradient(135deg, #ff6f1a 0%, #e9461e 100%)',
								fontFamily: 'Poppins',
								fontSize: '12px',
								fontWeight: 700,
								boxShadow: '0 7px 16px rgba(239, 89, 24, 0.24)',
								outline: 'none !important',
								'&:hover': {
									background: 'linear-gradient(135deg, #f26518 0%, #d93f1a 100%)',
								},
								'&:focus, &:focus-visible': {
									outline: 'none !important',
								},
							}}
						>
							SUBMIT
						</Button>

						{/* snack bar */}

						<Snackbar
							open={openSnackbar}
							autoHideDuration={1500} // Adjust the duration as needed
							onClose={handleSnackbarClose}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
						>
							<Alert onClose={handleSnackbarClose} severity="success">
								Feedback Recorded successfully.
							</Alert>
						</Snackbar>
						{/* error 401 snackbar */}
						<Snackbar
							open={error401Snackbar}
							autoHideDuration={1500}
							onClose={handleSnackbarClose}
							anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
						>
							<Alert
								onClose={handleSnackbarClose}
								severity="error"
								sx={{
									width: '100%',
									background: 'red',
									color: 'white',
									fontFamily: 'Poppins',
									'& .MuiAlert-icon': {
										color: 'white', // Change icon color to white
									},
								}}
							>
								Error 401
							</Alert>
						</Snackbar>
						<Snackbar
							open={openEmptyFieldSnackbar}
							autoHideDuration={1000}
							onClose={handleSnackbarClose}
							anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
						>
							<Alert
								onClose={handleSnackbarClose}
								severity="error"
								sx={{
									width: '100%',
									background: 'red',
									color: 'white',
									fontFamily: 'Poppins',
									'& .MuiAlert-icon': {
										color: 'white', // Change icon color to white
									},
								}}
							>
								Fill All the Fields
							</Alert>
						</Snackbar>
					</Box>
				</Grid>
			</Grid>
		</ThemeProvider>
	);
};

FeedbackPopup.propTypes = {
	onClose: PropTypes.func.isRequired, // onClose should be a function and required
};

export default FeedbackPopup;
